import React, { useEffect, useState, createElement } from 'react';
import { ArrowRightIcon, TrendingUpIcon, SearchIcon, ShieldCheckIcon, UsersIcon, CalendarCheckIcon, FileTextIcon, BarChart3Icon, CheckCircleIcon, ChevronDownIcon, ChevronUpIcon, WrenchIcon, HomeIcon, HeartPulseIcon, UtensilsIcon, DumbbellIcon, StoreIcon, HammerIcon, BuildingIcon, CarIcon, ScissorsIcon, BikeIcon, BedDoubleIcon, CookingPotIcon, BriefcaseIcon, PhoneIcon, StarIcon, MapPinIcon, MessageSquareIcon, CameraIcon, LaptopIcon, ShoppingCartIcon } from 'lucide-react';
import { FinalCtaSection } from './FinalCtaSection';
// Icon mapping helper
const IconMap: Record<string, React.ElementType> = {
  ShieldCheckIcon,
  SearchIcon,
  TrendingUpIcon,
  UsersIcon,
  CalendarCheckIcon,
  FileTextIcon,
  BarChart3Icon,
  WrenchIcon,
  HomeIcon,
  HeartPulseIcon,
  UtensilsIcon,
  DumbbellIcon,
  StoreIcon,
  HammerIcon,
  BuildingIcon,
  CarIcon,
  ScissorsIcon,
  BikeIcon,
  BedDoubleIcon,
  CookingPotIcon,
  BriefcaseIcon,
  PhoneIcon,
  StarIcon,
  CheckCircleIcon,
  MapPinIcon,
  MessageSquareIcon,
  CameraIcon,
  LaptopIcon,
  ShoppingCartIcon
};
export interface ClusterItem {
  name: string;
  slug: string;
  description: string;
  label: string;
}
export interface WhyItem {
  icon: string;
  title: string;
  description: string;
}
export interface FeatureItem {
  icon: string;
  title: string;
  description: string;
}
export interface FaqItem {
  q: string;
  a: string;
}
export interface RelatedPage {
  name: string;
  href: string;
}
export interface SectorPageProps {
  sectorName: string;
  sectorSlug: string;
  heroTitle: string;
  heroDescription: string;
  whyTitle: string;
  whyDescription: string;
  whyItems: WhyItem[];
  features: FeatureItem[];
  clusters: ClusterItem[];
  faqs: FaqItem[];
  relatedPages: RelatedPage[];
}
export function SectorPageTemplate({
  sectorName,
  sectorSlug,
  heroTitle,
  heroDescription,
  whyTitle,
  whyDescription,
  whyItems,
  features,
  clusters,
  faqs,
  relatedPages
}: SectorPageProps) {
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  const scrollToContact = (e: React.MouseEvent) => {
    e.preventDefault();
    document.getElementById('contact')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = `${sectorName} | Website laten maken | Noord`;
    // Meta description
    const desc = heroDescription.length > 155 ? heroDescription.substring(0, 155) + '...' : heroDescription;
    let metaDesc = document.querySelector('meta[name="description"]') as HTMLMetaElement;
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.name = 'description';
      document.head.appendChild(metaDesc);
    }
    metaDesc.content = desc;
    // Open Graph meta tags
    const ogTags: Record<string, string> = {
      'og:title': `${sectorName} | Website laten maken | Noord`,
      'og:description': desc,
      'og:type': 'website',
      'og:url': `https://noorddigital.nl/sector/${sectorSlug}`,
      'og:site_name': 'Noord Digital',
      'og:locale': 'nl_NL'
    };
    Object.entries(ogTags).forEach(([property, content]) => {
      let tag = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement;
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute('property', property);
        document.head.appendChild(tag);
      }
      tag.content = content;
    });
    // JSON-LD: FAQPage schema
    if (faqs.length > 0) {
      const faqSchema = {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: faqs.map(faq => ({
          '@type': 'Question',
          name: faq.q,
          acceptedAnswer: {
            '@type': 'Answer',
            text: faq.a
          }
        }))
      };
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.text = JSON.stringify(faqSchema);
      script.id = 'ld-faq-sector';
      document.head.appendChild(script);
      return () => {
        const el = document.getElementById('ld-faq-sector');
        if (el) el.remove();
      };
    }
  }, [sectorName, faqs]);
  const toggleFaq = (index: number) => {
    setOpenFaq(openFaq === index ? null : index);
  };
  return <main className="pt-16 lg:pt-[72px]" data-id="element-910">
      {/* Hero Section */}
      <section className="relative py-20 lg:py-32 bg-navy-900 overflow-hidden" data-id="element-911">
        <div className="absolute inset-0 pointer-events-none" data-id="element-912">
          <div className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full opacity-20 blur-3xl bg-accent" data-id="element-913" />
          <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full opacity-10 blur-3xl bg-accent-light" data-id="element-914" />
          <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
          backgroundSize: '32px 32px'
        }} data-id="element-915" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-916">
          <div className="max-w-3xl" data-id="element-917">
            <p className="text-accent-light font-semibold text-sm tracking-widest uppercase mb-4 animate-fade-in-up" data-id="element-918">
              {sectorName}
            </p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white mb-6 animate-fade-in-up-delay-1" data-id="element-919">
              {heroTitle}
            </h1>
            <p className="text-lg lg:text-xl text-navy-200 max-w-2xl mb-8 animate-fade-in-up-delay-2" data-id="element-920">
              {heroDescription}
            </p>
            <div className="flex flex-wrap gap-4 animate-fade-in-up-delay-3" data-id="element-921">
              <a href="#contact" onClick={scrollToContact} className="inline-flex items-center gap-2 bg-accent hover:bg-accent-hover text-white font-bold px-8 py-4 rounded-button shadow-medium hover:shadow-card transition-all duration-200 text-lg" data-id="element-922">
                Plan een gesprek
                <ArrowRightIcon className="w-5 h-5" data-id="element-923" />
              </a>
              <a href="#clusters" onClick={e => {
              e.preventDefault();
              document.getElementById('clusters')?.scrollIntoView({
                behavior: 'smooth'
              });
            }} className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white font-semibold px-6 py-4 rounded-button transition-all duration-200 border border-white/20" data-id="element-924">
                Bekijk specialisaties
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Waarom Section */}
      <section className="py-24 bg-surface-muted" data-id="element-925">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-926">
          <div className="text-center mb-16" data-id="element-927">
            <h2 className="text-3xl lg:text-4xl font-bold text-navy-900 mb-4" data-id="element-928">
              {whyTitle}
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto" data-id="element-929">
              {whyDescription}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-930">
            {whyItems.map((item, index) => {
            const Icon = IconMap[item.icon] || StarIcon;
            return <div key={index} className="bg-white p-8 rounded-card shadow-soft" data-id="element-931">
                  <div className="w-12 h-12 rounded-xl bg-accent-light flex items-center justify-center mb-6" data-id="element-932">
                    <Icon className="w-6 h-6 text-accent" data-id="element-933" />
                  </div>
                  <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-934">
                    {item.title}
                  </h3>
                  <p className="text-slate-600 leading-relaxed" data-id="element-935">
                    {item.description}
                  </p>
                </div>;
          })}
          </div>
        </div>
      </section>

      {/* Clusters Section */}
      <section id="clusters" className="py-24 bg-white" data-id="element-936">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-937">
          <div className="text-center mb-16" data-id="element-938">
            <h2 className="text-3xl lg:text-4xl font-bold text-navy-900 mb-4" data-id="element-939">
              Onze specialisaties binnen {sectorName}
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto" data-id="element-940">
              Kies jouw specifieke vakgebied voor een website die perfect
              aansluit bij jouw doelgroep.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-941">
            {clusters.map(cluster => <a key={cluster.slug} href={`#/sector/${sectorSlug}/${cluster.slug}`} className="group bg-surface-muted rounded-2xl p-8 hover:bg-navy-50 hover:shadow-medium transition-all duration-300 border border-transparent hover:border-navy-100" data-id="element-942">
                <div className="flex items-start justify-between mb-4" data-id="element-943">
                  <span className="inline-block px-3 py-1 bg-accent-light text-accent text-xs font-bold uppercase tracking-wider rounded-full" data-id="element-944">
                    {cluster.label}
                  </span>
                  <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center shadow-sm group-hover:bg-accent group-hover:text-white transition-colors" data-id="element-945">
                    <ArrowRightIcon className="w-5 h-5 text-navy-400 group-hover:text-white" data-id="element-946" />
                  </div>
                </div>
                <h3 className="text-2xl font-bold text-navy-900 mb-3 group-hover:text-accent transition-colors" data-id="element-947">
                  Website voor {cluster.name.toLowerCase()}
                </h3>
                <p className="text-slate-600 leading-relaxed" data-id="element-948">
                  {cluster.description}
                </p>
              </a>)}
          </div>
        </div>
      </section>

      {/* Belangrijke functionaliteiten */}
      <section className="py-24 bg-surface-muted" data-id="element-949">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-950">
          <div className="text-center mb-16" data-id="element-951">
            <h2 className="text-3xl lg:text-4xl font-bold text-navy-900 mb-4" data-id="element-952">
              Belangrijke functionaliteiten
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto" data-id="element-953">
              Functionaliteiten die onmisbaar zijn voor een website in{' '}
              {sectorName.toLowerCase()}.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-954">
            {features.map((feature, index) => {
            const Icon = IconMap[feature.icon] || StarIcon;
            return <div key={index} className="bg-white border border-navy-100 rounded-card p-8 text-center shadow-soft" data-id="element-955">
                  <div className="w-14 h-14 rounded-xl bg-accent-light flex items-center justify-center mx-auto mb-6" data-id="element-956">
                    <Icon className="w-7 h-7 text-accent" data-id="element-957" />
                  </div>
                  <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-958">
                    {feature.title}
                  </h3>
                  <p className="text-slate-600 leading-relaxed" data-id="element-959">
                    {feature.description}
                  </p>
                </div>;
          })}
          </div>
        </div>
      </section>

      {/* FAQ */}
      {faqs.length > 0 && <section className="py-24 bg-white" data-id="element-960">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-961">
            <div className="text-center mb-16" data-id="element-962">
              <h2 className="text-3xl lg:text-4xl font-bold text-navy-900 mb-4" data-id="element-963">
                Veelgestelde vragen
              </h2>
            </div>

            <div className="space-y-4" data-id="element-964">
              {faqs.map((faq, index) => <div key={index} className="bg-surface-muted rounded-xl overflow-hidden" data-id="element-965">
                  <button onClick={() => toggleFaq(index)} className="w-full px-6 py-5 flex items-center justify-between text-left focus:outline-none" data-id="element-966">
                    <span className="font-bold text-navy-900 pr-4" data-id="element-967">
                      {faq.q}
                    </span>
                    {openFaq === index ? <ChevronUpIcon className="w-5 h-5 text-accent flex-shrink-0" data-id="element-968" /> : <ChevronDownIcon className="w-5 h-5 text-navy-400 flex-shrink-0" data-id="element-969" />}
                  </button>
                  {openFaq === index && <div className="px-6 pb-5 text-slate-600 leading-relaxed" data-id="element-970">
                      {faq.a}
                    </div>}
                </div>)}
            </div>
          </div>
        </section>}

      {/* Gerelateerde pagina's */}
      <section className="py-16 bg-surface-muted border-y border-navy-100" data-id="element-971">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-972">
          <h3 className="text-lg font-semibold text-navy-900 text-center mb-8" data-id="element-973">
            Gerelateerde pagina's
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl mx-auto" data-id="element-974">
            {relatedPages.map((page, index) => <a key={index} href={page.href} className="flex items-center justify-between bg-white p-5 rounded-card border border-navy-100 hover:shadow-medium transition-all group" data-id="element-975">
                <span className="font-medium text-navy-900" data-id="element-976">{page.name}</span>
                <ArrowRightIcon className="w-4 h-4 text-accent group-hover:translate-x-1 transition-transform" data-id="element-977" />
              </a>)}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <FinalCtaSection data-id="element-978" />
    </main>;
}